import classnames from 'classnames';
import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { useWindowSize } from '@/hooks/useWindowSize';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { STREAK_STATUSES } from '@/helpers/constants';
import { MyLink } from '@/components/MyLink';
import { APP_LINKS } from '@/helpers/links';
import IconUp from './icon_up.svg';
import IconDown from './icon_down.svg';
import IconPause from './icon_pause.svg';
import IconChart from './icon_chart.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const LS_KEY = 'streak_status_show';
const id = MODAL_IDS.streak_status_modal;

export const StreakStatusModal = ({ data }) => {
  const { openSingleModal, modalIds } = useModal();

  useEffect(() => {
    try {
      if (data) {
        const currentState = localStorage.getItem(LS_KEY);

        if (!currentState) {
          if (data.streak > 0) {
            data.status !== STREAK_STATUSES.not_started && openStreakModal();
          }
          updateLocalStorage();
          return;
        }

        const [status, streak] = currentState.split(':');

        if (
          status + streak !== data.status + data.streak &&
          data.status !== STREAK_STATUSES.not_started
        ) {
          openStreakModal();
          updateLocalStorage();
        }
      }
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }, [data]);

  const updateLocalStorage = () => {
    localStorage.setItem(LS_KEY, [data.status, data.streak].join(':'));
  };

  const openStreakModal = () => {
    setTimeout(() => {
      openSingleModal(MODAL_IDS.streak_status_modal);
    }, 1000);
  };

  return modalIds[id] && data && <Modal data={data} />;
};

const Modal = ({ data }) => {
  const { closeModal } = useModal();
  const { isMobile } = useWindowSize();
  const { t } = useTranslation('streak');

  const onClose = () => {
    closeModal(id);
  };

  const { streak, title, description, icon } = useMemo(() => {
    switch (data.status) {
      case STREAK_STATUSES.active:
        if (data.streak >= data.max_milestone) {
          return {
            streak: data.streak,
            title: t('streak_modal.streak_increased'),
            description: t('streak_modal.streak_completed'),
            icon: IconUp,
          };
        }

        return {
          streak: data.streak,
          title: t('streak_modal.streak_increased'),
          description: (
            <Trans
              ns="streak"
              i18nKey="streak_modal.streak_increased_description"
              values={{ streak: data.streak }}
              components={{ Span: <span className="f-500" /> }}
            />
          ),
          icon: IconUp,
        };
      case STREAK_STATUSES.paused:
        return {
          streak: data.streak,
          title: t('streak_modal.streak_paused'),
          description: t('streak_modal.streak_paused_description'),
          icon: IconPause,
        };
      case STREAK_STATUSES.broken:
        return {
          streak: data.streak,
          title: t('streak_modal.streak_broken'),
          description: t('streak_modal.streak_broken_description'),
          icon: IconDown,
        };
      default:
        return {
          streak: 0,
          title: '',
          description: '',
          icon: IconPause,
        };
    }
  }, [data]);

  const content = (
    <div className={s.modal}>
      <div className={s.background}>
        <div className={s.circle}>
          <div className={s.circle_inner}>
            <span className="f-hero-h2">{streak}</span>
          </div>
          <div className={s.icon}>
            <img src={icon} alt="icon" width={12} height={12} />
          </div>
        </div>
      </div>
      <div className={s.content}>
        <div className="f-article-accent">{title}</div>
        <div className="f-small-regular">{description}</div>
      </div>
      <div className={s.buttons}>
        <MyLink
          type="button"
          className={s.btn_small}
          href={APP_LINKS.investmentStreakActivity}
        >
          <img src={IconChart} alt="icon" width={20} height={20} />
        </MyLink>
        <button
          type="button"
          onClick={onClose}
          className={classnames(r.black_large, s.btn_continue)}
        >
          {t('streak_modal.continue')}
        </button>
      </div>
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalCenterLayout>
  );
};
